import { formatCurrency } from './common';

/**
 * The AFFORDANCE for a market price the platform could not retrieve, never
 * the guarantee. `POST /api/orders/create-order/` already refuses a stale
 * TRACKED listing server-side regardless of what this file returns
 * (`apps/api/apps/orders/views.py`, "unavailable_listing_ids") -- this only
 * stops a buyer from wasting a click on something the server will reject
 * anyway. Never treat this as protection, and never let a UI state assume
 * the server will agree with it.
 *
 * A TRACKED listing prices off the market, and `price_stale` -- present on
 * every serialized `CardListing` -- says whether that market read is recent
 * enough to sell on. A FIXED listing is a price the seller typed directly,
 * never server-derived, so staleness never applies to it: treating one as
 * blocked would be a silent revenue regression.
 */
export function isPriceUnavailable(listing) {
  return Boolean(listing?.pricing_mode === 'tracked' && listing?.price_stale);
}

/**
 * "DID ANYONE EVEN ASK TODAY?" -- a DIFFERENT question from the one above, and
 * the two are unioned, never substituted for one another.
 *
 * `price_unconfirmed` is server-computed as `not is_price_fresh(price_checked_at)`
 * (`apps/api/apps/cards/pricing.py`, `is_listing_price_unconfirmed`): it says the
 * card was not ATTEMPTED today. `isPriceUnavailable` reads `price_stale`, which
 * is keyed on `price_retrieved_at`: it says nothing was successfully RETRIEVED
 * inside the seven-day sellability horizon.
 *
 * NEITHER SET CONTAINS THE OTHER, so every caller THAT CAN ACT ON THE ANSWER
 * must read
 *
 *     isPriceUnconfirmed(item) || isPriceUnavailable(item)
 *
 * and none of them may read one of the two alone. An earlier design was safe reading
 * a single predicate because both of its halves keyed on `price_retrieved_at`;
 * that containment is gone. `_refresh` stamps `price_checked_at` on three exits
 * that retrieve nothing (`catalog/pricing.py`: no external ref, fetch failure,
 * and delisted-after-fetch), so a delisted or ref-less card is re-stamped every
 * day forever -- permanently `unavailable ∧ ¬unconfirmed`. Swapping this in for
 * `isPriceUnavailable` would let exactly that cohort commit SILENTLY, deleting
 * the guard issue #110 shipped rather than overriding it.
 *
 * FIXED listings are excluded for the same reason as above: the figure is one
 * the seller typed, never derived from a market read, so "was the market
 * checked today" is not a question about it.
 *
 * An ABSENT field is FALSE, deliberately. Old cached payloads, and any caller
 * that never threads the key, must behave exactly as they do today -- the
 * marker is not allowed to appear on a surface that was simply never told.
 *
 * TWO SURFACES READ `isPriceUnavailable` ALONE, ON PURPOSE. Both are named
 * here because the sentence above is exactly the kind a later reader
 * "corrects" into a regression:
 *
 *   - CHECKOUT (`Checkout.jsx`'s `isCheckoutItemBlocked`, and the server's
 *     `create_order_and_payment` it mirrors). Sellability is the card-wide
 *     horizon and nothing else; a client that refused MORE than the server
 *     would cancel sales rather than save clicks.
 *   - THE BUY BOX, `/marketplace/card/:cardId/:listId`
 *     (`Marketplace/ListDetail/CardDetail.jsx`). It duplicates the cart write
 *     inline, never calls `useAddToCart`, and so cannot run the refresh that
 *     turns this marker into a question. Unioning there would dead-end a
 *     merely-unchecked listing on a toast with nothing behind it -- a marker
 *     the buyer cannot act on, which is the flaw two earlier designs were
 *     refuted for (spec 3.6). It gets the union in the same change that gives
 *     it the flow, or not at all.
 */
export function isPriceUnconfirmed(listing) {
  return Boolean(listing?.pricing_mode === 'tracked' && listing?.price_unconfirmed);
}

/**
 * WHY THIS DOES NOT READ `price_basis_confirmed`, AND MUST NOT.
 * -------------------------------------------------------------
 * There are TWO questions in this feature and they have different answers.
 * Conflating them looks like a missing check and is actually a policy change.
 *
 *   1. "MAY I SELL THIS?" -- the gate above, on initial load. The server's
 *      answer is `is_listing_price_unsellable` (`apps/api/apps/cards/pricing.py`),
 *      which refuses on the CARD-WIDE staleness horizon and nothing else;
 *      `create_order_and_payment` is built on it. `price_stale` alone is the
 *      exact mirror of that, which is the only thing this predicate is allowed
 *      to be -- it exists to save a click on something the server will refuse,
 *      so a client that refused MORE than the server would not be saving a
 *      click, it would be cancelling a sale the platform was happy to make.
 *
 *   2. "DID I JUST CONFIRM *THIS* LISTING'S PRICE?" -- BOTH fields, and only
 *      in the seconds after a `refresh-card-prices` round trip. See
 *      `useAddToCart`'s "WHAT COUNTS AS CONFIRMED": a refresh that returned
 *      Near Mint rows and nothing for a Damaged listing flips `price_stale`
 *      to false while that listing's own figure was never re-derived, so a
 *      client that had just asked for a confirmation must read both before
 *      telling the buyer it got one.
 *
 * THE ASYMMETRY IS DELIBERATE. Widening this predicate to
 * `price_stale || price_basis_confirmed === false` was proposed on final
 * review (2026-08-31) and REJECTED by the product owner. A tracked listing at
 * a (finish, condition) JustTCG never prices is PERMANENTLY
 * `price_basis_confirmed: false` -- no refresh can ever change that -- so the
 * widened gate would block a real, sellable cohort forever, on a surface the
 * server would have sold from. That is a seller-facing revenue harm and a
 * sellability POLICY decision; it belongs with the product owner and issue
 * #111's permanent-vs-transient work, not with a UI predicate.
 *
 * So `price_basis_confirmed` is threaded to the surfaces that carry listings
 * (see `normaliseListing` in `Marketplace/CardDetail/CardDetail.jsx`) and is
 * simply not consulted here. If a future change wants it in this gate, the
 * question to settle FIRST is what the server does with that cohort -- not
 * what the UI does.
 */

/**
 * Shared copy for the guard above. The server's create-order rejection uses
 * the sentence-form message below verbatim; every surface that talks about
 * this state -- the add-to-cart refusal, the checkout row note, the
 * checkout-time toast -- is built from `PRICE_UNAVAILABLE_PHRASE` so one
 * edit changes them together. Wording is an OPEN product decision (task-11
 * brief, "Open decisions carried from the spec" #3, 2026-08-29) -- do not
 * invent a different phrase here; change this file instead once it's settled.
 */
export const PRICE_UNAVAILABLE_PHRASE = 'Price could not be retrieved';

export const PRICE_UNAVAILABLE_ADD_TO_CART_MESSAGE = `${PRICE_UNAVAILABLE_PHRASE} for this listing yet. Please try again shortly.`;

export const PRICE_UNAVAILABLE_CHECKOUT_MESSAGE = `${PRICE_UNAVAILABLE_PHRASE} for one or more items.`;

/**
 * TASK 3 -- copy for `PriceConfirmDialog` (`components/UI/PriceConfirmDialog`),
 * which renders `useAddToCart`'s `confirm` descriptor. Lives here for the
 * same reason as `PRICE_UNAVAILABLE_PHRASE` above: this file is already the
 * one place this state's wording is written down, so the dialog's copy
 * cannot drift from it by living inline in a component instead.
 *
 * MODE: 'moved'. The refresh confirmed a real, CURRENT price -- just not the
 * one the tile showed. `priceMovedDetail` is null-safe on BOTH figures:
 *
 *   - `oldPrice`: `useAddToCart`'s JSDoc types the descriptor's `oldPrice` as
 *     `number|null` (ruling 5, task-3 brief) -- a caller that built the item
 *     without a `price` at all reaches this same mode with nothing to
 *     compare against, and "Was No price, now R650.00" is a worse sentence
 *     than simply not claiming to know what it was.
 *   - `newPrice`: fix round 1, review finding on `priceMovedDetail`. Not
 *     reachable today -- `CardListing.price` is a `NOT NULL` column, so a row
 *     that reaches 'moved' mode always carries a real `fresh.price` -- but
 *     `formatCurrency(null)` returns the STRING `"No price"`, and rendering
 *     "Was R610.00, now No price." beside a live Continue button would tell
 *     the buyer their tap adds the item at a price that is, in the same
 *     sentence, said not to exist. Same class of dishonesty ruling 5 exists
 *     to prevent, just on the other figure -- so it is guarded the same way,
 *     defensively, rather than left to the day the assumption stops holding.
 */
export const PRICE_MOVED_TITLE = 'Price updated';
export const PRICE_MOVED_MESSAGE =
  "This listing's price changed since it was shown to you. Continue to add it at the new price.";
export const PRICE_MOVED_CONTINUE_LABEL = 'Continue';
export const PRICE_MOVED_CANCEL_LABEL = 'Cancel';

export function priceMovedDetail(oldPrice, newPrice) {
  const oldKnown = oldPrice != null;
  const newKnown = newPrice != null;
  if (oldKnown && newKnown)
    return `Was ${formatCurrency(oldPrice)}, now ${formatCurrency(newPrice)}.`;
  if (newKnown) return `Now ${formatCurrency(newPrice)}.`;
  /* Neither figure claims anything false on its own, but "Was R610.00." with
     no "now" reads as an unfinished sentence beside a Continue button that is
     about to add the item at a price this line just declined to state --
     so when the new figure is unknown, say nothing about price at all rather
     than half of it. The caller renders this paragraph only when it is
     non-empty. */
  return '';
}

/**
 * MODE: 'unconfirmable'. TWO different server-side causes land here, and the
 * copy has to be honest about both, not just the one its name suggests
 * (ruling 3, task-3 brief):
 *
 *   - the refresh could not re-derive THIS listing's own price -- the
 *     upstream fetch failed outright, or came back with rows for a different
 *     finish/condition than this listing's own (see `useAddToCart`'s "WHAT
 *     COUNTS AS CONFIRMED").
 *   - the listing SOLD OUT. `refresh-card-prices` filters `listed_cards` on
 *     `quantity__gt=0` (`apps/api/apps/cards/views.py`), so a sold-out
 *     listing is simply ABSENT from the response -- indistinguishable, from
 *     here, from a price that could not be confirmed. There is no separate
 *     "sold out" branch to write copy for; one sentence has to cover both.
 *
 * NO RETRY INVITATION (ruling 4). `get_fresh` returns early once today's
 * refresh has already run, and stamps that fact even on a FAILED refresh, so
 * a same-day "try again" can provably do nothing until the next UTC
 * midnight -- unlike `PRICE_UNAVAILABLE_ADD_TO_CART_MESSAGE` above, whose
 * "Please try again shortly" this copy does not reuse. The dismiss-only
 * button already makes that honest; the words have to as well.
 *
 * NO "CHECK BACK TOMORROW" EITHER (fix round 1 review finding). An earlier
 * draft of this sentence ended on that instead, which is honest for only ONE
 * of the causes above -- a listing that is unapproved, archived, deleted or
 * genuinely sold out (`refresh-card-prices`' own query:
 * `status="approved", quantity__gt=0, is_deleted=False, is_archived=False`,
 * `apps/api/apps/cards/views.py`) will not exist tomorrow either, and "check
 * back" tells that buyer to come back to nothing. The durable advice --
 * shop elsewhere for this card -- is true for every cause in the bucket, so
 * it leads; the temporal promise is dropped rather than softened, since any
 * phrasing of "maybe tomorrow" is still a claim this sentence cannot back for
 * three of its four causes.
 */
export const PRICE_UNCONFIRMABLE_TITLE = 'Price could not be confirmed';
export const PRICE_UNCONFIRMABLE_MESSAGE =
  'Browse other sellers for this card. This listing may have sold out or become unavailable, or its price could not be confirmed.';
export const PRICE_UNCONFIRMABLE_DISMISS_LABEL = 'Dismiss';

/**
 * THE SALE THE DIALOG ABOVE MUST NOT REFUSE (spec 3.5, Task 3).
 * --------------------------------------------------------------
 * The not-confirmed outcome splits by SELLABILITY. When the refreshed row
 * comes back `price_stale: false` -- inside the seven-day horizon
 * `create_order_and_payment` refuses on -- the server would sell this listing
 * happily, so answering the buyer with the dismiss-only dialog above would not
 * save them a click, it would CANCEL a sale. That branch adds the item and
 * says this instead.
 *
 * IT IS NOT `PRICE_UNCONFIRMABLE_MESSAGE`, and must never be. That sentence
 * sends the buyer to another seller because nothing here can be bought; this
 * one accompanies a successful add. Reusing it would tell a buyer to shop
 * elsewhere for a card that is, at that moment, in their cart.
 *
 * NO RETRY INVITATION, for the same reason the dismiss-only copy carries none:
 * `get_fresh` returns early once today's refresh has run and stamps that fact
 * even on a FAILED refresh, so a same-day "try again" provably does nothing
 * until the next UTC midnight.
 *
 * IT MAKES NO CLAIM ABOUT THE CART. `commitToCart` owns that sentence ("Added
 * to cart", or the partial-add count) and can still refuse the write on its
 * own clamps -- sold out, or the maximum already in the cart -- so this line
 * qualifies the PRICE and nothing else, and stays honest either way.
 *
 * THE "may change" HALF IS LITERAL, not a hedge. The cart stores only
 * `{listing_id, quantity}` (`hooks/useAddToCart.js`) and checkout re-hydrates
 * every row against fresh listing data, so the figure the buyer is charged is
 * whatever the server holds then -- which, for a listing nothing re-derived,
 * is exactly the figure that has not been confirmed.
 */
export const PRICE_UNCONFIRMED_ADDED_MESSAGE =
  "We could not re-confirm this listing's price. It is the last figure we had, and may change before checkout.";

/**
 * WHICH LISTING this question is about (fix round 1 review finding,
 * IMPORTANT 2). `PriceConfirmDialog` used to render only a title, a generic
 * message and a price pair -- nothing naming the card or its condition. Two
 * blocked listings that differ only by condition (a Near Mint and a Damaged
 * copy of the same card) then produced two dialogs a buyer could not tell
 * apart by re-reading them, and `useAddToCart.openConfirm` silently
 * supersedes an outgoing question the instant a second one opens (not just
 * when they share a card -- ANY two blocked adds in flight at once race this
 * way) -- so a buyer who tapped twice and read only the SECOND dialog could
 * press Continue believing they were answering the FIRST tap they remember
 * making, and commit money to the wrong listing.
 *
 * `cardName`/`condition` come from `CardListingWithSellerSerializer`'s
 * `card_name`/`condition` fields by way of the refreshed row `useAddToCart`
 * already holds (see that file's `buildUnconfirmable` and the 'moved'
 * descriptor) -- no backend change, no new request. Either can be `null`:
 * the REJECTION path has no row at all (the request never answered), and a
 * sold-out/archived/deleted listing has no row either (absent from
 * `listed_cards`). Never print "undefined" for either case -- the caller
 * gets back `undefined` (renders nothing) rather than a label built out of a
 * missing half.
 */
export function confirmSubjectLabel(cardName, condition) {
  const parts = [cardName, condition].filter(Boolean);
  if (parts.length === 0) return undefined;
  return parts.join(' · ');
}

/**
 * TASK 4 -- the marker a blocked STORE TILE carries before anyone taps it.
 *
 * The tile is not a dialog and cannot explain itself in a sentence, so this is
 * a short qualifier that sits beside the price figure and is folded into the
 * quick-add button's accessible name. It is deliberately NOT built from
 * `PRICE_UNAVAILABLE_PHRASE` ("Price could not be retrieved"): that phrase is
 * an outcome the buyer has already run into, past tense, and it is twice the
 * length a tile has room for. On a tile nothing has been attempted yet -- the
 * honest thing to say is that the figure below it is not confirmed, which is
 * exactly what `price_stale` on a TRACKED listing means.
 *
 * IT NEVER REPLACES THE FIGURE (plan §8 item 13, ruling 2). Suppressing the
 * price was considered and rejected: a whole storefront can go stale at once
 * when a refresh run fails, and a shelf of priceless tiles is a seller-facing
 * harm far larger than the buyer-facing one it would fix. The figure stays and
 * this qualifies it.
 *
 * VISUAL TREATMENT IS AN OPEN QUESTION, and deliberately not decided here.
 * The brief leaves "what the unconfirmed marker actually looks like on a tile"
 * to the design-system owner; it must not be settled from the API shape. So
 * this is text, and the card components render it as text with no colour, icon
 * or badge language of their own.
 */
export const PRICE_UNCONFIRMED_TILE_NOTE = 'Price unconfirmed';
